import React from 'react';
import { Box, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { ESG_FIGURE, ESG_FIGURE_BASE, type EsgFigureSize } from '../../constants/typography';
import { formatDelta, formatDeltaMagnitude, type DeltaKind } from '../../utils/numberFormat';
import { deltaTone, type MetricPolarity } from '../../utils/metricPolarity';
import AnimatedFigure from './AnimatedFigure';

export interface KpiDelta {
  /** Relative change in % (`relative`) or absolute change in percentage points (`points`). */
  value: number | null | undefined;
  kind?: DeltaKind;
  polarity: MetricPolarity;
  /** e.g. "vs previous month" */
  comparisonLabel?: string;
}

export interface KpiFigureProps {
  label: React.ReactNode;
  /** Prefer an `ESG_ICONS` component; a rendered icon element is also accepted. */
  icon?: SvgIconComponent | React.ReactElement;
  /** Pre-formatted figure (see `useNumberFormat`); counts up on mount and on change. */
  value: string;
  unit?: string;
  size?: EsgFigureSize;
  /** Caption under the figure (period, basis, scope). */
  context?: React.ReactNode;
  /** Secondary value line (e.g. cost). */
  secondary?: React.ReactNode;
  delta?: KpiDelta;
  /** Explanatory tooltip on an info icon next to the label. */
  info?: React.ReactNode;
  /** Trailing control in the label row (e.g. overflow menu). */
  action?: React.ReactNode;
  loading?: boolean;
  /** Extra content under the figure (e.g. a target bar). */
  children?: React.ReactNode;
  id?: string;
}

const LABEL_ICON_SIZE: Record<EsgFigureSize, number> = { hero: 20, lg: 18, md: 18, sm: 16 };

/**
 * Editorial KPI: the number carries the hierarchy, the icon only classifies.
 *
 *   [icon] LABEL
 *   48,920 t
 *   Completed pickups · March 2026
 *   ↑ 12.8% vs previous month
 */
const KpiFigure: React.FC<KpiFigureProps> = ({
  label,
  icon: Icon,
  value,
  unit,
  size = 'md',
  context,
  secondary,
  delta,
  info,
  action,
  loading = false,
  children,
  id,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const latin = (i18n.language || 'en').startsWith('en');
  const prominent = size === 'hero' || size === 'lg';
  const inlineUnit = unit === '%';

  // Shown as "↑ 100%": the arrow carries the direction; the signed text stays for screen readers.
  const deltaText = delta ? formatDeltaMagnitude(delta.value, i18n.language) : null;
  const deltaSpoken = delta
    ? `${formatDelta(delta.value, delta.kind ?? 'relative', i18n.language) ?? ''}${
        delta.kind === 'points' ? ` ${t('dashboard.delta.points')}` : ''
      }`
    : '';
  const tone = delta ? deltaTone(delta.value, delta.polarity) : 'neutral';
  const toneColor =
    tone === 'positive'
      ? theme.palette.success.main
      : tone === 'negative'
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const DeltaIcon =
    delta && typeof delta.value === 'number' && delta.value !== 0
      ? delta.value > 0
        ? ArrowUpwardRounded
        : ArrowDownwardRounded
      : null;

  return (
    <Box id={id} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          {Icon ? (
            <Box
              aria-hidden
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                color: prominent ? 'primary.main' : 'text.secondary',
                '& .MuiSvgIcon-root': { fontSize: LABEL_ICON_SIZE[size], color: 'inherit' },
              }}
            >
              {React.isValidElement(Icon) ? Icon : React.createElement(Icon as SvgIconComponent)}
            </Box>
          ) : null}
          <Typography
            variant={prominent ? 'overline' : 'body2'}
            component="h3"
            sx={{
              m: 0,
              color: 'text.secondary',
              fontWeight: prominent ? 600 : 500,
              lineHeight: 1.4,
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
          {info ? (
            <Tooltip title={info} placement="top">
              <InfoOutlined
                tabIndex={0}
                aria-label={typeof info === 'string' ? info : undefined}
                sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0 }}
              />
            </Tooltip>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0, mt: -0.75, mx: -0.75 }}>{action}</Box> : null}
      </Box>

      <Box sx={{ mt: prominent ? 1.5 : 1 }}>
        {loading ? (
          <Skeleton variant="rounded" width="60%" sx={{ height: { hero: 52, lg: 36, md: 28, sm: 24 }[size] }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0 }}>
            <Typography
              component="p"
              sx={{
                ...ESG_FIGURE_BASE,
                ...ESG_FIGURE[size],
                m: 0,
                color: 'text.primary',
                letterSpacing: latin && prominent ? '-0.02em' : 0,
              }}
            >
              <Box component="bdi" sx={{ unicodeBidi: 'isolate' }}>
                <AnimatedFigure value={value} />
                {inlineUnit ? (
                  <Box component="span" sx={{ fontSize: '0.6em', fontWeight: 500, color: 'text.secondary', ml: 0.25 }}>
                    {unit}
                  </Box>
                ) : null}
              </Box>
            </Typography>
            {unit && !inlineUnit ? (
              <Typography
                component="span"
                sx={{
                  fontSize: prominent ? '1rem' : '0.875rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}
              >
                {unit}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>

      {secondary ? (
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 500 }}>
          {secondary}
        </Typography>
      ) : null}

      {context ? (
        <Typography variant="caption" component="p" sx={{ mt: 0.75, color: 'text.secondary' }}>
          {context}
        </Typography>
      ) : null}

      {delta && deltaText ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          {DeltaIcon ? <DeltaIcon aria-hidden sx={{ fontSize: 16, color: toneColor }} /> : null}
          <Typography variant="body2" component="span" sx={{ color: toneColor, fontWeight: 600 }}>
            <Box component="bdi" aria-hidden sx={{ unicodeBidi: 'isolate', fontVariantNumeric: 'tabular-nums' }}>
              {deltaText}
            </Box>
            <Box
              component="span"
              sx={{ position: 'absolute', width: '1px', height: '1px', m: '-1px', p: 0, border: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
            >
              {deltaSpoken}
            </Box>
          </Typography>
          {delta.comparisonLabel ? (
            <Typography variant="body2" component="span" color="text.secondary">
              {delta.comparisonLabel}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {children}
    </Box>
  );
};

export default KpiFigure;
